import moment from "moment"

/**
 * @typedef {Object} Schedule
 * @property {string} [start_on] - Schedule start date
 * @property {string} [end_on] - Schedule end date
 */

/**
 * @typedef {Object} DateRange
 * @property {Date|null} minDate - Minimum allowed date
 * @property {Date|null} maxDate - Maximum allowed date
 */

// Constants
const DATE_FORMAT = {
  START_OF_DAY: "day",
  START_OF_WEEK: "week",
}

/**
 * Validates if a date string is a valid date
 * @param {string|undefined} dateStr - Date string to validate
 * @returns {boolean} - Whether the date is valid
 */
const isValidDate = dateStr => {
  if (!dateStr) return false
  const date = moment(dateStr)
  return date.isValid()
}

/**
 * Creates a moment date object set to start of day
 * @returns {moment.Moment} Moment object set to start of current day
 */
export const getStartOfToday = () => moment().startOf(DATE_FORMAT.START_OF_DAY)

/**
 * Creates a moment date object set to start of week
 * @returns {moment.Moment} Moment object set to start of current week
 */
export const getStartOfWeek = () => moment().startOf(DATE_FORMAT.START_OF_WEEK)

/**
 * Calculates the allowed date range for calendar assistant based on schedule(s)
 * @param {Schedule[]} schedules - Array of schedule objects
 * @returns {DateRange} Date range object
 * @throws {Error} If schedules parameter is not an array
 */
export const getCalendarAssistantDateRange = schedules => {
  // Input validation
  if (!Array.isArray(schedules)) {
    throw new Error("schedules parameter must be an array")
  }

  // For multiple schedules, no date restrictions apply
  if (schedules.length !== 1) {
    return { minDate: null, maxDate: null }
  }

  // Extract schedule data with default empty object
  const [schedule = {}] = schedules
  const { start_on, end_on } = schedule

  // Calculate date constraints
  const earliestAllowedDate = getStartOfWeek()
  const hasValidStartDate = isValidDate(start_on)
  const hasValidEndDate = isValidDate(end_on)

  // Determine minimum date
  const minDate =
    hasValidStartDate && earliestAllowedDate.isBefore(start_on)
      ? moment(start_on).toDate()
      : earliestAllowedDate.toDate()

  // Determine maximum date
  const maxDate = hasValidEndDate ? moment(end_on).toDate() : undefined

  return { minDate, maxDate }
}
